import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const GRID_SIZE = 5;
const CENTER = 2;

/**
 * Builds a spiral path from the center outward for a 5×5 grid (minus center).
 * This gives a smooth "ripple from center" effect.
 */
const SPIRAL_PATH: [number, number][] = [
  // Ring 1 (adjacent to center) - clockwise from top
  [1, 2], [1, 3], [2, 3], [3, 3], [3, 2], [3, 1], [2, 1], [1, 1],
  // Ring 2 (outer edge) - clockwise from top-left
  [0, 0], [0, 1], [0, 2], [0, 3], [0, 4],
  [1, 4], [2, 4], [3, 4], [4, 4],
  [4, 3], [4, 2], [4, 1], [4, 0],
  [3, 0], [2, 0], [1, 0],
];

const TOTAL_CELLS = SPIRAL_PATH.length; // 24
const ANIM_DURATION = 2.4; // seconds per full cycle

const SPIRAL_MAP = new Map<string, number>();
SPIRAL_PATH.forEach(([r, c], i) => SPIRAL_MAP.set(`${r}-${c}`, i));

const KEYFRAME_NAME = 'gitfit-grid-ripple';

/** Contribution-green color palette indices mapped to ring distance */
const getContribLevel = (row: number, col: number): number => {
  const dist = Math.max(Math.abs(row - CENTER), Math.abs(col - CENTER));
  // Ring 1 = brightest (level 4), Ring 2 = medium (level 2)
  if (dist === 1) return 4;
  return 2;
};

const STYLE_SHEET = `
@keyframes ${KEYFRAME_NAME} {
  0%   { opacity: 0.06; transform: scale(0.7); }
  12%  { opacity: 1; transform: scale(1.05); }
  28%  { opacity: 0.5; transform: scale(1); }
  50%  { opacity: 0.06; transform: scale(0.7); }
  100% { opacity: 0.06; transform: scale(0.7); }
}
`;

interface GitFitGridLoaderProps {
  /** Total width/height in pixels (default 70) */
  size?: number;
  className?: string;
}

const GitFitGridLoader: React.FC<GitFitGridLoaderProps> = ({
  size = 70,
  className = '',
}) => {
  const { t } = useTranslation();

  const gapPx = Math.max(2, Math.round(size * 0.06));
  const cellRadius = Math.round(size * 0.06);

  const cells = useMemo(() => {
    const result: {
      row: number;
      col: number;
      spiralIndex: number | null;
      contribLevel: number;
    }[] = [];
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (row === CENTER && col === CENTER) continue;
        result.push({
          row,
          col,
          spiralIndex: SPIRAL_MAP.get(`${row}-${col}`) ?? null,
          contribLevel: getContribLevel(row, col),
        });
      }
    }
    return result;
  }, []);

  return (
    <>
      <style>{STYLE_SHEET}</style>
      <div
        className={className}
        style={{
          display: 'inline-grid',
          gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
          gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`,
          gap: `${gapPx}px`,
          width: size,
          height: size,
        }}
        role="status"
        aria-label={t('common.loading', 'Loading')}
      >
        {cells.map(({ row, col, spiralIndex, contribLevel }) => (
          <div
            key={`${row}-${col}`}
            style={{
              gridRow: row + 1,
              gridColumn: col + 1,
              borderRadius: cellRadius,
              backgroundColor: `hsl(var(--contrib-${contribLevel}))`,
              opacity: 0.06,
              animation: spiralIndex !== null
                ? `${KEYFRAME_NAME} ${ANIM_DURATION}s cubic-bezier(0.4, 0, 0.2, 1) infinite`
                : undefined,
              animationDelay: spiralIndex !== null
                ? `${((spiralIndex / TOTAL_CELLS) * ANIM_DURATION).toFixed(3)}s`
                : undefined,
              willChange: 'opacity, transform',
            }}
          />
        ))}
      </div>
    </>
  );
};

export default GitFitGridLoader;
